<template>
  <div class="space-y-3">
    <!-- Compact toolbar above image -->
    <div class="flex items-center gap-2">
      <!-- Image navigation (fixed left) -->
      <div class="flex items-center shrink-0">
        <button
          :disabled="currentIndex === 0"
          class="p-3 -m-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
          @click="prevImage">
          <fa-icon icon="chevron-left" class="text-sm" />
        </button>
        <span class="px-1 text-xs text-gray-400 tabular-nums">
          {{ currentIndex + 1 }}/{{ totalImages }}
        </span>
        <button
          :disabled="currentIndex >= totalImages - 1"
          class="p-3 -m-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
          @click="nextImage">
          <fa-icon icon="chevron-right" class="text-sm" />
        </button>
      </div>

      <!-- Spacer -->
      <div class="flex-1" />

      <!-- Zoom/Rotate controls (show in edit mode) -->
      <div v-if="isEditMode" class="flex items-center gap-1">
        <!-- Reset button (only when changed, inside expanded controls) -->
        <button
          v-if="hasTransform"
          class="px-2 py-1 text-xs text-indigo-400 transition-colors hover:text-indigo-300"
          @click="resetTransform">
          reset
        </button>

        <div v-if="hasTransform" class="w-px h-4 bg-gray-700 mx-0.5" />

        <!-- Zoom buttons -->
        <button
          :disabled="zoom <= 1.0"
          class="p-1.5 text-gray-400 hover:text-white disabled:opacity-30"
          title="Zoom out"
          @click="zoomOut">
          <fa-icon icon="minus" class="text-xs" />
        </button>
        <button
          :disabled="zoom >= 3"
          class="p-1.5 text-gray-400 hover:text-white disabled:opacity-30"
          title="Zoom in"
          @click="zoomIn">
          <fa-icon icon="plus" class="text-xs" />
        </button>

        <!-- <div class="w-px h-4 bg-gray-700 mx-0.5" /> -->

        <!-- Rotation buttons (temporarily disabled) -->
        <!-- <button
          @click="rotateLeft"
          class="p-1.5 text-gray-400 hover:text-white"
          title="Rotate left">
          <fa-icon icon="rotate-left" class="text-xs" />
        </button>
        <button
          @click="rotateRight"
          class="p-1.5 text-gray-400 hover:text-white"
          title="Rotate right">
          <fa-icon icon="rotate-right" class="text-xs" />
        </button> -->

        <!-- <div class="w-px h-4 bg-gray-700 mx-0.5" /> -->
      </div>

      <!-- Feature buttons (fixed right) -->
      <div class="flex items-center shrink-0">
        <!-- Mode toggle: Move (swipe images) vs Edit (adjust image) -->
        <button
          class="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border-2 transition-all duration-200 touch-manipulation font-medium text-gray-300 hover:text-white border-gray-600 hover:border-gray-500 hover:bg-gray-700/50"
          :title="
            isEditMode
              ? 'Exit edit mode (enable image swiping)'
              : 'Enter edit mode (pan/zoom image)'
          "
          @click="toggleEditMode">
          <fa-icon
            :icon="isEditMode ? 'arrows-left-right' : 'crop'"
            class="text-sm" />
          <span>{{ isEditMode ? 'Browse' : 'Edit' }}</span>
        </button>
      </div>
    </div>

    <!-- Canvas Container - 4:3 aspect ratio -->
    <div
      ref="containerRef"
      class="relative w-full overflow-hidden bg-gray-900 rounded-lg select-none touch-none"
      style="aspect-ratio: 4/3"
      @mousedown="startPan"
      @mousemove="onPan"
      @mouseup="endPan"
      @mouseleave="endPan"
      @dblclick="onDoubleClick"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @wheel="onWheel">
      <!-- Preview images carousel - shown in browse mode -->
      <template v-if="!isEditMode">
        <!-- Container for carousel effect -->
        <div
          class="absolute inset-0 flex"
          :style="carouselContainerStyle">
          <!-- Previous image (peeking from left) -->
          <div
            v-if="prevImageUrl"
            class="absolute inset-0 w-full h-full shrink-0"
            style="left: -100%">
            <img
              :src="prevImageUrl"
              class="object-cover w-full h-full"
              :style="prevImageStyle"
              draggable="false" />
            <div
              v-if="scoreColor"
              class="absolute inset-0 pointer-events-none"
              :style="{
                backgroundColor: scoreColor,
                mixBlendMode: 'multiply'
              }" />
          </div>

          <!-- Current image -->
          <div class="relative w-full h-full shrink-0">
            <img
              v-if="currentImageUrl"
              :src="currentImageUrl"
              class="object-cover w-full h-full"
              :class="canvasCursorClass"
              :style="currentImageStyle"
              draggable="false"
              @load="onPreviewImageLoad" />
            <!-- Color overlay for score tint (multiply blend) -->
            <div
              v-if="scoreColor"
              class="absolute inset-0 pointer-events-none"
              :style="{
                backgroundColor: scoreColor,
                mixBlendMode: 'multiply'
              }" />
            <!-- Text overlay for browse mode (matches ReviewCard layout) -->
            <div :class="overlayClasses.container">
              <!-- Left side: username above title -->
              <div
                v-if="titleText || username"
                :class="overlayClasses.titleContainer">
                <div :class="overlayClasses.titleWrapper">
                  <!-- Username row -->
                  <div
                    v-if="username"
                    class="flex flex-wrap gap-2 items-center">
                    <span :class="overlayClasses.username">
                      @{{ username }}
                    </span>
                  </div>
                  <!-- Title row -->
                  <div v-if="titleText" class="text-white truncate">
                    <span :class="overlayClasses.title">
                      {{ titleText }}
                    </span>
                    <span v-if="year" :class="overlayClasses.subtitle">
                      {{ year }}
                    </span>
                  </div>
                </div>
              </div>
              <!-- Right side: score -->
              <span
                v-if="score !== null && score !== undefined"
                :class="overlayClasses.score">
                {{ Number(score).toFixed(1) }}
              </span>
            </div>
          </div>

          <!-- Next image (peeking from right) -->
          <div
            v-if="nextImageUrl"
            class="absolute inset-0 w-full h-full shrink-0"
            style="left: 100%">
            <img
              :src="nextImageUrl"
              class="object-cover w-full h-full"
              :style="nextImageStyle"
              draggable="false" />
            <div
              v-if="scoreColor"
              class="absolute inset-0 pointer-events-none"
              :style="{
                backgroundColor: scoreColor,
                mixBlendMode: 'multiply'
              }" />
          </div>
        </div>
      </template>

      <!-- Canvas - only shown in edit mode for pan/zoom/rotate -->
      <canvas
        v-show="isEditMode"
        ref="canvasRef"
        class="absolute inset-0 w-full h-full transition-all duration-200 bg-gray-800/20"
        :class="canvasCursorClass"
        :style="{ display: containerWidth > 0 ? 'block' : 'none' }" />

      <!-- Edit mode grab handles (non-touch devices only) -->
      <transition name="fade">
        <div
          v-if="isEditMode && !isTouchDevice && !isLoading"
          class="absolute inset-0 pointer-events-none">
          <!-- Corner handles for rotation -->
          <div
            v-for="corner in ['tl', 'tr', 'bl', 'br']"
            :key="corner"
            class="absolute w-8 h-8 pointer-events-auto group"
            :class="{
              'top-2 left-2': corner === 'tl',
              'top-2 right-2': corner === 'tr',
              'bottom-2 left-2': corner === 'bl',
              'bottom-2 right-2': corner === 'br'
            }"
            @mousedown.stop="startRotateHandle($event, corner)">
            <!-- Corner L-shape -->
            <div
              class="absolute transition-all duration-150"
              :class="[
                rotateHandleActive
                  ? 'opacity-80'
                  : 'opacity-30 group-hover:opacity-70',
                corner === 'tl' ? 'top-0 left-0' : '',
                corner === 'tr' ? 'top-0 right-0' : '',
                corner === 'bl' ? 'bottom-0 left-0' : '',
                corner === 'br' ? 'bottom-0 right-0' : ''
              ]">
              <div
                class="absolute rounded-full shadow-sm bg-white/90"
                :class="[
                  corner.includes('t') ? 'top-0' : 'bottom-0',
                  corner.includes('l') ? 'left-0' : 'right-0',
                  'w-4 h-1'
                ]" />
              <div
                class="absolute rounded-full shadow-sm bg-white/90"
                :class="[
                  corner.includes('t') ? 'top-0' : 'bottom-0',
                  corner.includes('l') ? 'left-0' : 'right-0',
                  'w-1 h-4'
                ]" />
            </div>
            <!-- Rotation cursor area -->
            <div
              class="absolute inset-0"
              :class="{
                'cursor-[url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNCA0TDIwIDIwTTE2IDRIMjBWOE00IDIwVjE2SDgiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+PC9zdmc+)_12_12,pointer]':
                  corner === 'tl' || corner === 'br',
                'cursor-[url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMjAgNEw0IDIwTTggNEg0VjhNMjAgMjBWMTZIMTYiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+PC9zdmc+)_12_12,pointer]':
                  corner === 'tr' || corner === 'bl'
              }" />
          </div>

          <!-- Edge handles for zoom -->
          <div
            v-for="edge in ['t', 'b', 'l', 'r']"
            :key="edge"
            class="absolute pointer-events-auto group"
            :class="{
              'top-2 left-1/2 -translate-x-1/2 w-12 h-6': edge === 't',
              'bottom-2 left-1/2 -translate-x-1/2 w-12 h-6': edge === 'b',
              'left-2 top-1/2 -translate-y-1/2 w-6 h-12': edge === 'l',
              'right-2 top-1/2 -translate-y-1/2 w-6 h-12': edge === 'r'
            }"
            @mousedown.stop="startZoomHandle($event, edge)">
            <!-- Edge bar -->
            <div
              class="absolute transition-all duration-150 rounded-full shadow-sm bg-white/90"
              :class="[
                zoomHandleActive
                  ? 'opacity-80'
                  : 'opacity-30 group-hover:opacity-70',
                edge === 't' || edge === 'b'
                  ? 'left-1/2 -translate-x-1/2 w-8 h-1'
                  : 'top-1/2 -translate-y-1/2 w-1 h-8',
                edge === 't' ? 'top-1' : '',
                edge === 'b' ? 'bottom-1' : '',
                edge === 'l' ? 'left-1' : '',
                edge === 'r' ? 'right-1' : ''
              ]" />
            <!-- Cursor -->
            <div
              class="absolute inset-0"
              :class="{
                'cursor-ns-resize': edge === 't' || edge === 'b',
                'cursor-ew-resize': edge === 'l' || edge === 'r'
              }" />
          </div>
        </div>
      </transition>

      <!-- Loading overlay - only show when canvas is loading in edit mode -->
      <div
        v-if="isLoading && isEditMode"
        class="absolute inset-0 flex items-center justify-center bg-gray-900/50">
        <fa-icon
          icon="spinner-third"
          class="text-2xl text-white animate-spin" />
      </div>

      <!-- Gesture hint - only show briefly on first load -->
      <transition name="fade">
        <div
          v-if="showHint && !isLoading"
          class="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            class="px-4 py-3 text-xs text-center rounded-lg text-white/90 bg-black/70 max-w-48">
            <template v-if="isEditMode">
              <p class="font-medium">Edit Mode</p>
              <p v-if="isTouchDevice" class="mt-1 text-white/60">
                Drag to move • Pinch to zoom
              </p>
              <p v-else class="mt-1 text-white/60">
                Drag to move • Use handles to zoom/rotate
              </p>
            </template>
            <template v-else>
              <p class="font-medium">Browse Mode</p>
              <p class="mt-1 text-white/60">Swipe to change images</p>
            </template>
          </div>
        </div>
      </transition>

      <!-- Swipe indicator (move mode) -->
      <transition name="fade">
        <div
          v-if="isSwipingImages && Math.abs(swipeOffset) > 20"
          class="absolute inset-0 flex items-center pointer-events-none">
          <!-- Left arrow indicator (swipe right = prev) -->
          <div
            v-if="swipeOffset > 0 && currentIndex > 0"
            class="absolute p-3 text-white transition-opacity rounded-full left-4 bg-black/60"
            :style="{ opacity: Math.min(1, swipeOffset / SWIPE_THRESHOLD) }">
            <fa-icon icon="chevron-left" class="text-lg" />
          </div>
          <!-- Right arrow indicator (swipe left = next) -->
          <div
            v-if="swipeOffset < 0 && currentIndex < totalImages - 1"
            class="absolute p-3 text-white transition-opacity rounded-full right-4 bg-black/60"
            :style="{
              opacity: Math.min(1, Math.abs(swipeOffset) / SWIPE_THRESHOLD)
            }">
            <fa-icon icon="chevron-right" class="text-lg" />
          </div>
        </div>
      </transition>

      <!-- Debug/reinit button when canvas fails in edit mode (only show after init delay) -->
      <div
        v-if="isEditMode && canvasInitFailed && (containerWidth === 0 || !imageRef)"
        class="absolute inset-0 z-40 flex items-center justify-center">
        <div class="p-4 space-y-3 text-center rounded-lg bg-black/90">
          <p class="text-sm text-white">Canvas not initialized</p>
          <p class="font-mono text-xs text-left text-gray-400">
            container: {{ debugInfo.containerWidth }}x{{
              debugInfo.containerHeight
            }}<br />
            image: {{ debugInfo.imageLoaded ? 'loaded' : 'not loaded' }}<br />
            loading: {{ debugInfo.isLoading ? 'yes' : 'no' }}<br />
            cors: {{ debugInfo.imageCors ? 'yes' : 'no' }}<br />
            url: {{ currentImageUrl ? 'set' : 'none' }}
          </p>
          <button
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg"
            @click="forceReinit">
            Retry Load
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { Capacitor } from '@capacitor/core'
import QRCode from 'qrcode'
import { getWebOverlayClasses } from '@/composables/useReviewTextOverlay'

// Get CSS classes from composable for consistent styling with ReviewCard
const overlayClasses = getWebOverlayClasses()

// Spectra "S" logo only (first character from logotype) - viewBox: 0 0 226 243
const SPECTRA_S_LOGO_PATH =
  'M11.6,225.3c-4-2-5.3-4-5.3-9.6v-48.1c0-9,2.7-10.3,9-6,18.2,12.6,53.1,21.6,91.2,21.6s56.4-5,56.4-18.6-30.5-12.9-71.3-15.3C48.4,147,0,136,0,74.3S43.5,0,109.8,0s75,7.3,94.2,16.9,5.3,4,5.3,9.6v48.1c0,9-2.7,10.3-9,6-18.2-12.6-46.5-20.9-90.6-20.9s-46.8,4.3-46.8,17.9,24.9,12.3,54.4,13.9c51.8,3,108.5,10.6,108.5,76.3s-44.8,75-119.4,75-75.7-8-94.9-17.6Z'

// Spectra full logotype SVG path data - viewBox: 0 0 1379 303.9
const SPECTRA_LOGO_PATH =
  'M11.6,225.3c-4-2-5.3-4-5.3-9.6v-48.1c0-9,2.7-10.3,9-6,18.2,12.6,53.1,21.6,91.2,21.6s56.4-5,56.4-18.6-30.5-12.9-71.3-15.3C48.4,147,0,136,0,74.3S43.5,0,109.8,0s75,7.3,94.2,16.9,5.3,4,5.3,9.6v48.1c0,9-2.7,10.3-9,6-18.2-12.6-46.5-20.9-90.6-20.9s-46.8,4.3-46.8,17.9,24.9,12.3,54.4,13.9c51.8,3,108.5,10.6,108.5,76.3s-44.8,75-119.4,75-75.7-8-94.9-17.6Z M255.8,297.3V65c0-3.7,3-6.6,6.6-6.6h46.5c3.6,0,6.6,3,6.6,6.6v6.6c12.3-11.9,28.9-18.6,48.8-18.6,55.4,0,88.6,37.8,88.6,95.2s-33.2,94.2-88.6,94.2-36.5-6.6-48.8-18.3v73c0,3.6-3,6.6-6.6,6.6h-46.5c-3.6,0-6.6-3-6.6-6.6ZM393.2,148c0-25.2-15.3-41.8-38.8-41.8s-38.8,16.6-38.8,41.8,15.3,41.5,38.8,41.5,38.8-16.3,38.8-41.5Z M476.1,148c0-60.1,37.2-94.9,97.5-94.9s87.9,40.1,87.9,108.5-3,6.3-6.6,6.3h-116.8c5.3,17.6,18.2,28.5,42.1,28.5s43.5-5.3,60.4-12.9c3.6-1.7,7.6.3,7.6,4.3v38.5c0,2.3-1.3,4.6-3.7,5.6-15.6,6.6-42.5,10.9-67.7,10.9-59.7,0-100.9-37.5-100.9-94.9ZM604.2,130.1c0-16.6-15.3-27.2-30.5-27.2s-30.5,10.3-35.5,27.2h66Z M684.9,148c0-57.4,41.8-94.9,105.5-94.9s47.8,4.3,59.7,11.3c2.3,1.3,4.3,3,4.3,5.6v48.1c0,4-4,6-7.6,4-13.9-7.6-34.2-12.6-56.4-12.6s-45.8,15.3-45.8,38.5,18.2,38.5,45.8,38.5,40.1-5.3,57.1-12.9c3.6-1.7,7.6.3,7.6,4.3v48.4c0,2.3-1.3,4.6-3.7,5.6-15.6,6.6-37.8,10.9-61.1,10.9-63.7,0-105.5-37.5-105.5-94.9Z M905.8,157.6v-52.8h-27.5c-3.7,0-6.6-3-6.6-6.6v-33.2c0-3.7,3-6.6,6.6-6.6h27.5V11.9c0-3.7,3-6.6,6.6-6.6h46.5c3.7,0,6.6,3,6.6,6.6v46.5h46.5c3.6,0,6.6,3,6.6,6.6v33.2c0,3.6-3,6.6-6.6,6.6h-46.5v52.8c0,36.2,20.6,35.8,52.8,29.5,4-.7,8,1.7,8,5.6l.7,36.8c0,2.3-1.3,4.6-3.7,5.6-10.9,4.6-26.2,7.6-42.1,7.6-49.4,0-75.3-29.2-75.3-85.3Z M1050.2,230.6V65c0-3.7,3-6.6,6.6-6.6h46.5c3.6,0,6.6,3,6.6,6.6v11.3c13.9-13.6,32.5-21.6,55.1-22.9,4-.3,7,2.7,7,6.6v46.1c0,3.6-3,6.6-7,6.6-38.5,1-55.1,10.9-55.1,34.8v83c0,3.7-3,6.6-6.6,6.6h-46.5c-3.6,0-6.6-3-6.6-6.6Z M1181.9,147.3c0-57.1,33.2-94.2,88.6-94.2s36.5,6.6,48.8,18.2v-6.3c0-3.7,3-6.6,6.6-6.6h46.5c3.6,0,6.6,3,6.6,6.6v165.9c0,3.6-3,6.6-6.6,6.6h-46.5c-3.7,0-6.6-3-6.6-6.6v-7c-12.3,11.9-28.9,18.6-48.8,18.6-55.4,0-88.6-37.8-88.6-95.2ZM1319.3,147.7c0-25.2-15.3-41.5-38.8-41.5s-38.8,16.3-38.8,41.5,15.3,41.8,38.8,41.8,38.8-16.6,38.8-41.8Z'

const props = defineProps({
  images: {
    type: Array,
    default: () => []
  },
  initialIndex: {
    type: Number,
    default: 0
  },
  tmdbImageBase: {
    type: String,
    default: 'https://image.tmdb.org/t/p/original'
  },
  scoreColor: {
    type: String,
    default: null
  },
  tintOpacity: {
    type: Number,
    default: 0.5
  },
  // Text overlay props
  titleText: {
    type: String,
    default: ''
  },
  username: {
    type: String,
    default: ''
  },
  score: {
    type: [Number, String],
    default: null
  },
  // Year for title display
  year: {
    type: [Number, String],
    default: null
  },
  // Review ID for QR code link
  reviewId: {
    type: String,
    default: ''
  },
  // Initial transform state for restoring existing review edits
  initialTransform: {
    type: Object,
    default: null
  },
  // Initial image path to select (for existing reviews)
  initialImagePath: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:imagePath', 'imageReady'])

// Canvas dimensions - tracks actual container size
const containerWidth = ref(0)
const containerHeight = ref(0)

// Canvas initialization state
const canvasInitAttempts = ref(0)
const MAX_CANVAS_INIT_ATTEMPTS = 15
const CANVAS_INIT_RETRY_DELAY = 50

// Update container dimensions with retry logic for iOS
const updateContainerDimensions = () => {
  if (containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) {
      containerWidth.value = rect.width
      containerHeight.value = rect.height
      canvasInitAttempts.value = 0 // Reset on success
      return true
    }
  }

  // Retry if dimensions not ready
  canvasInitAttempts.value++
  if (canvasInitAttempts.value < MAX_CANVAS_INIT_ATTEMPTS) {
    const delay =
      CANVAS_INIT_RETRY_DELAY * Math.min(canvasInitAttempts.value, 3)
    setTimeout(() => {
      requestAnimationFrame(updateContainerDimensions)
    }, delay)
  } else {
    // Max attempts reached, mark as failed
    canvasInitFailed.value = true
  }
  return false
}

// Refs
const containerRef = ref(null)
const canvasRef = ref(null)
const imageRef = ref(null)

// Grayscale canvas cache - avoids re-running expensive pixel manipulation
const cachedGrayscaleCanvas = ref(null)
const cachedGrayscaleImageSrc = ref(null)

// State
const currentIndex = ref(props.initialIndex)
const isLoading = ref(false)
const isPanning = ref(false)
const showHint = ref(true)
const showTextOverlay = ref(true)
// Edit mode: true = pan/zoom/rotate image, false = swipe to change images
const isEditMode = ref(false)
// Track if canvas init has failed (only show debug after delay)
const canvasInitFailed = ref(false)

// Detect touch device
const isTouchDevice = ref(false)

// Controls visibility is now handled by isEditMode

// Debug state
const showDebug = ref(false)
let debugTapCount = 0
let debugTapTimeout = null

// Transform state
const panX = ref(0)
const panY = ref(0)
const zoom = ref(1)
const rotation = ref(0)

// Touch gesture tracking
const touchState = ref({
  startDistance: 0,
  startRotation: 0,
  startZoom: 1,
  startRotationValue: 0,
  lastTouchCount: 0
})

// Pan tracking
const panStart = ref({ x: 0, y: 0 })
const panStartOffset = ref({ x: 0, y: 0 })

// Swipe tracking for move mode (changing images)
const swipeStartX = ref(0)
const swipeOffset = ref(0)
const isSwipingImages = ref(false)
const SWIPE_THRESHOLD = 80 // px to trigger image change

// Handle dragging state (for desktop rotation/zoom handles)
const rotateHandleActive = ref(false)
const zoomHandleActive = ref(false)
const handleStartAngle = ref(0)
const handleStartRotation = ref(0)
const handleStartY = ref(0)
const handleStartZoom = ref(1)
const activeEdge = ref(null)

// Double-tap detection for touch devices
const lastTapTime = ref(0)
const lastTapX = ref(0)
const lastTapY = ref(0)
const DOUBLE_TAP_DELAY = 300 // ms between taps
const DOUBLE_TAP_DISTANCE = 30 // max pixels between taps

// Computed
const totalImages = computed(() => props.images?.length || 0)

// Cursor class based on mode
const canvasCursorClass = computed(() => {
  if (isEditMode.value) {
    return isPanning.value ? 'cursor-grabbing' : 'cursor-grab'
  } else {
    return isSwipingImages.value ? 'cursor-grabbing' : 'cursor-ew-resize'
  }
})

const hasTransform = computed(() => {
  return (
    panX.value !== 0 ||
    panY.value !== 0 ||
    zoom.value !== 1 ||
    rotation.value !== 0
  )
})

// Preview image style - CSS transforms for immediate feedback (browse mode only)
// In browse mode, we show transforms via CSS; in edit mode, canvas handles it
const previewImageStyle = computed(() => {
  // Apply grayscale and color overlay via CSS filters when score color is set
  const filters = []
  if (props.scoreColor) {
    filters.push('grayscale(1)', 'brightness(1.25)')
  }

  // Build transform string
  const transforms = []
  if (zoom.value !== 1) {
    transforms.push(`scale(${zoom.value})`)
  }
  if (rotation.value !== 0) {
    transforms.push(`rotate(${rotation.value}deg)`)
  }
  if (panX.value !== 0 || panY.value !== 0) {
    transforms.push(`translate(${panX.value}px, ${panY.value}px)`)
  }

  return {
    filter: filters.length > 0 ? filters.join(' ') : undefined,
    transform: transforms.length > 0 ? transforms.join(' ') : undefined
  }
})

// Track if canvas has been initialized for current image
const canvasReady = ref(false)

// Preview image loaded handler
const onPreviewImageLoad = () => {
  // Mark that preview is ready - canvas not needed yet
  isLoading.value = false
  // Notify parent of current image path
  emit('update:imagePath', currentImagePath.value)
}

const currentImagePath = computed(() => {
  if (!props.images || props.images.length === 0) return null
  return props.images[currentIndex.value]?.file_path
})

const currentImageUrl = computed(() => {
  if (!currentImagePath.value) return null
  return `${props.tmdbImageBase}${currentImagePath.value}`
})

// Previous and next image URLs for carousel
const prevImageUrl = computed(() => {
  if (!props.images || currentIndex.value <= 0) return null
  const prevPath = props.images[currentIndex.value - 1]?.file_path
  return prevPath ? `${props.tmdbImageBase}${prevPath}` : null
})

const nextImageUrl = computed(() => {
  if (!props.images || currentIndex.value >= props.images.length - 1) return null
  const nextPath = props.images[currentIndex.value + 1]?.file_path
  return nextPath ? `${props.tmdbImageBase}${nextPath}` : null
})

// Carousel animation state
const isCarouselAnimating = ref(false)

// Carousel container style - handles swipe translation
const carouselContainerStyle = computed(() => {
  const translateX = swipeOffset.value
  return {
    transform: `translateX(${translateX}px)`,
    transition: isCarouselAnimating.value ? 'transform 0.3s ease-out' : 'none'
  }
})

// Current image style (for pan/zoom transforms applied in edit mode, preserved in browse mode)
const currentImageStyle = computed(() => {
  const filters = []
  if (props.scoreColor) {
    filters.push('grayscale(1)', 'brightness(1.25)')
  }

  const transforms = []
  if (zoom.value !== 1) {
    transforms.push(`scale(${zoom.value})`)
  }
  if (rotation.value !== 0) {
    transforms.push(`rotate(${rotation.value}deg)`)
  }
  if (panX.value !== 0 || panY.value !== 0) {
    transforms.push(`translate(${panX.value}px, ${panY.value}px)`)
  }

  return {
    filter: filters.length > 0 ? filters.join(' ') : undefined,
    transform: transforms.length > 0 ? transforms.join(' ') : undefined
  }
})

// Prev/next image styles (just the color filter, no pan/zoom)
const prevImageStyle = computed(() => {
  const filters = []
  if (props.scoreColor) {
    filters.push('grayscale(1)', 'brightness(1.25)')
  }
  return {
    filter: filters.length > 0 ? filters.join(' ') : undefined
  }
})

const nextImageStyle = computed(() => {
  const filters = []
  if (props.scoreColor) {
    filters.push('grayscale(1)', 'brightness(1.25)')
  }
  return {
    filter: filters.length > 0 ? filters.join(' ') : undefined
  }
})

// Debug info for troubleshooting canvas issues
const debugInfo = computed(() => {
  const canvas = canvasRef.value
  const container = containerRef.value
  const img = imageRef.value

  return {
    containerWidth: containerWidth.value,
    containerHeight: containerHeight.value,
    canvasWidth: canvas?.width || 0,
    canvasHeight: canvas?.height || 0,
    imageWidth: img?.width || 0,
    imageHeight: img?.height || 0,
    imageLoaded: !!img,
    imageCors: imageLoadedWithCors.value,
    isLoading: isLoading.value,
    hasContainer: !!container,
    hasCanvas: !!canvas,
    hasImage: !!img,
    imageUrl: currentImageUrl.value ? 'set' : null
  }
})

// Debug tap handler - tap 5x quickly to show debug overlay
const onDebugTap = () => {
  debugTapCount++

  if (debugTapTimeout) {
    clearTimeout(debugTapTimeout)
  }

  if (debugTapCount >= 5) {
    showDebug.value = true
    debugTapCount = 0
  } else {
    // Reset count after 2 seconds of no taps
    debugTapTimeout = setTimeout(() => {
      debugTapCount = 0
    }, 2000)
  }
}

// Force reinitialize the canvas
const forceReinit = () => {
  // Reset state
  containerWidth.value = 0
  containerHeight.value = 0
  imageRef.value = null
  cachedGrayscaleCanvas.value = null
  cachedGrayscaleImageSrc.value = null
  canvasInitFailed.value = false
  canvasInitAttempts.value = 0

  // Force update dimensions and reload
  requestAnimationFrame(() => {
    updateContainerDimensions()
    loadImage()
  })
}

// Track if we've applied initial state (image index and transform)
const hasAppliedInitialState = ref(false)
// Track if we should skip reset on next image change (when applying initial image)
const skipNextReset = ref(false)

// Track if we need CORS (for canvas export) vs display-only
const imageLoadedWithCors = ref(false)

// Store blob URLs for cleanup
const blobUrlRef = ref(null)

// Cleanup blob URL when component unmounts or image changes
const cleanupBlobUrl = () => {
  if (blobUrlRef.value) {
    URL.revokeObjectURL(blobUrlRef.value)
    blobUrlRef.value = null
  }
}

/**
 * Check if we're running on Safari (including iOS Safari and iOS native app)
 */
const isSafari = () => {
  // Check for iOS native app first (Capacitor)
  if (Capacitor.getPlatform() === 'ios') {
    return true
  }
  // Check for Safari browser
  const ua = navigator.userAgent || ''
  return /^((?!chrome|android).)*safari/i.test(ua)
}

/**
 * Load image as a blob to bypass CORS issues in Safari
 * Safari is stricter about CORS and can taint canvas even with crossOrigin set
 * Using blob URLs ensures the image is same-origin
 */
const loadImageAsBlob = async (url) => {
  // Add cache-busting parameter for Safari to prevent using cached non-CORS responses
  const cacheBustUrl = isSafari()
    ? `${url}${url.includes('?') ? '&' : '?'}_cb=${Date.now()}`
    : url

  const response = await fetch(cacheBustUrl, {
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store' // Prevent caching issues
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`)
  }

  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

// Methods
const loadImage = async () => {
  if (!currentImageUrl.value) return

  isLoading.value = true
  cleanupBlobUrl() // Clean up previous blob URL

  const onImageLoaded = (img, withCors) => {
    // Ensure image is fully loaded with valid dimensions (especially important for iOS native)
    // Sometimes on iOS, naturalWidth/Height aren't immediately available even after onload
    const ensureImageReady = () => {
      if (
        !img.complete ||
        (img.naturalWidth === 0 && img.width === 0) ||
        (img.naturalHeight === 0 && img.height === 0)
      ) {
        // Image not ready yet, wait a bit and retry
        requestAnimationFrame(ensureImageReady)
        return
      }

      imageRef.value = img
      imageLoadedWithCors.value = withCors
      isLoading.value = false

      // Apply initial transform if we're loading the initial image
      if (skipNextReset.value) {
        skipNextReset.value = false
        // Apply the transform after the correct image is loaded
        if (props.initialTransform) {
          applyTransform(props.initialTransform)
        }
      } else if (!hasAppliedInitialState.value) {
        // First load and no initial state to apply
        resetTransform()
      }
      // Otherwise keep current transform (user navigated images)

      // Ensure container dimensions are ready before rendering (iOS timing)
      if (containerWidth.value <= 0 || containerHeight.value <= 0) {
        // Wait for dimensions to be available
        requestAnimationFrame(() => {
          updateContainerDimensions()
          renderCanvas()
        })
      } else {
        renderCanvas()
      }
      emit('update:imagePath', currentImagePath.value)
    }

    // Start checking if image is ready
    ensureImageReady()
  }

  // Try loading via blob first (works around Safari CORS canvas tainting)
  // This fetches the image as a blob and creates a same-origin blob URL
  try {
    const blobUrl = await loadImageAsBlob(currentImageUrl.value)
    blobUrlRef.value = blobUrl

    const img = new Image()
    img.onload = () => {
      onImageLoaded(img, true)
    }

    img.onerror = () => {
      console.warn('Blob image load failed, trying direct CORS load')
      cleanupBlobUrl()
      loadImageDirect()
    }

    img.src = blobUrl
  } catch (error) {
    console.warn('Blob fetch failed, trying direct CORS load:', error)
    loadImageDirect()
  }
}

// Fallback: direct image loading with CORS attribute
const loadImageDirect = () => {
  const onImageLoaded = (img, withCors) => {
    const ensureImageReady = () => {
      if (
        !img.complete ||
        (img.naturalWidth === 0 && img.width === 0) ||
        (img.naturalHeight === 0 && img.height === 0)
      ) {
        requestAnimationFrame(ensureImageReady)
        return
      }

      imageRef.value = img
      imageLoadedWithCors.value = withCors
      isLoading.value = false

      if (skipNextReset.value) {
        skipNextReset.value = false
        if (props.initialTransform) {
          applyTransform(props.initialTransform)
        }
      } else if (!hasAppliedInitialState.value) {
        resetTransform()
      }

      if (containerWidth.value <= 0 || containerHeight.value <= 0) {
        requestAnimationFrame(() => {
          updateContainerDimensions()
          renderCanvas()
        })
      } else {
        renderCanvas()
      }
      emit('update:imagePath', currentImagePath.value)
    }

    ensureImageReady()
  }

  const img = new Image()
  img.crossOrigin = 'anonymous'

  img.onload = () => {
    onImageLoaded(img, true)
  }

  img.onerror = () => {
    console.warn(
      'CORS image load failed, trying without CORS:',
      currentImageUrl.value
    )
    const imgNoCors = new Image()

    imgNoCors.onload = () => {
      onImageLoaded(imgNoCors, false)
    }

    imgNoCors.onerror = () => {
      console.error(
        'Failed to load image (both CORS and non-CORS):',
        currentImageUrl.value
      )
      isLoading.value = false
    }

    imgNoCors.src = currentImageUrl.value
  }

  img.src = currentImageUrl.value
}

// Image cache for faster switching
const imageCache = new Map()

// Optimized image loading with cache for navigation performance
const loadImageOptimized = async () => {
  if (!currentImageUrl.value) return

  // Check cache first
  const cachedImg = imageCache.get(currentImageUrl.value)
  if (cachedImg && cachedImg.complete) {
    imageRef.value = cachedImg
    imageLoadedWithCors.value = true
    isLoading.value = false
    renderCanvas()
    return
  }

  // Show loading for new images only
  isLoading.value = true

  try {
    // Preload next/prev images in background
    const currentIdx = currentIndex.value
    const preloadUrls = []
    if (currentIdx > 0 && props.images[currentIdx - 1]) {
      preloadUrls.push(props.images[currentIdx - 1].file_path)
    }
    if (currentIdx < props.images.length - 1 && props.images[currentIdx + 1]) {
      preloadUrls.push(props.images[currentIdx + 1].file_path)
    }

    // Load current image with blob for CORS
    const blobUrl = await loadImageAsBlob(currentImageUrl.value)
    blobUrlRef.value = blobUrl

    const img = new Image()
    img.onload = () => {
      // Cache the loaded image
      imageCache.set(currentImageUrl.value, img)

      imageRef.value = img
      imageLoadedWithCors.value = true
      isLoading.value = false
      renderCanvas()

      // Preload adjacent images in background
      preloadUrls.forEach((url) => preloadImage(url))
    }

    img.onerror = () => {
      console.warn('Optimized image load failed, falling back to standard load')
      loadImage()
    }

    img.src = blobUrl
  } catch (error) {
    console.warn('Optimized load failed, falling back:', error)
    loadImage()
  }
}

// Background preloader for adjacent images
const preloadImage = async (url) => {
  if (imageCache.has(url)) return

  try {
    const blobUrl = await loadImageAsBlob(url)
    const img = new Image()
    img.onload = () => {
      imageCache.set(url, img)
      URL.revokeObjectURL(blobUrl) // Clean up blob URL after caching
    }
    img.src = blobUrl
  } catch (error) {
    // Silent fail for preloading
  }
}

// Helper to parse HSL color string and return components
const parseHslColor = (hslString) => {
  if (!hslString) return null
  const match = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
  if (match) {
    return {
      h: parseInt(match[1]),
      s: parseInt(match[2]),
      l: parseInt(match[3])
    }
  }
  return null
}

// Create a darkened version of the color for the base layer
const getDarkenedColor = (hslString, darkenAmount = 0.4) => {
  const hsl = parseHslColor(hslString)
  if (!hsl) return '#111827'
  const newLightness = Math.max(hsl.l * darkenAmount, 5)
  return `hsl(${hsl.h}, ${hsl.s}%, ${newLightness}%)`
}

// Create a color with specific alpha for overlay
const getColorWithAlpha = (hslString, alpha) => {
  const hsl = parseHslColor(hslString)
  if (!hsl) return null
  return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${alpha})`
}

/**
 * Detect if we're on iOS (Safari or native app) which has limited canvas filter support
 */
const isIOSSafari = () => {
  // Check for iOS native app first (Capacitor)
  if (Capacitor.getPlatform() === 'ios') {
    return true
  }
  // Fallback to user agent detection for iOS Safari
  const ua = navigator.userAgent || navigator.vendor || window.opera
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream
}

/**
 * Check if canvas filter is supported
 */
const supportsCanvasFilter = () => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  return ctx && 'filter' in ctx
}

/**
 * Convert image to grayscale using pixel manipulation (iOS-compatible)
 * This is a fallback when ctx.filter is not supported
 * @param {HTMLImageElement} img - Source image
 * @param {number} brightness - Brightness multiplier (default 1.25)
 * @returns {HTMLCanvasElement} - Canvas with grayscale image
 */
const convertToGrayscalePixels = (img, brightness = 1.25) => {
  // Safety check for valid image and that it's fully loaded
  if (!img) {
    throw new Error('Image is null or undefined')
  }

  // Check if image is fully loaded - use naturalWidth/Height for actual dimensions
  // and complete to ensure it's loaded
  if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
    throw new Error('Image is not fully loaded')
  }

  // Use natural dimensions for accurate canvas size
  const imgWidth = img.naturalWidth || img.width
  const imgHeight = img.naturalHeight || img.height

  if (imgWidth === 0 || imgHeight === 0) {
    throw new Error('Invalid image dimensions')
  }

  const canvas = document.createElement('canvas')
  canvas.width = imgWidth
  canvas.height = imgHeight
  const ctx = canvas.getContext('2d')

  // Draw the image first - use natural dimensions
  ctx.drawImage(img, 0, 0, imgWidth, imgHeight)

  // Get pixel data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  // Convert to grayscale using luminance formula: 0.299*R + 0.587*G + 0.114*B
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]

    // Calculate grayscale value using luminance formula
    const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b)

    // Apply brightness adjustment
    const adjustedGray = Math.min(255, Math.round(gray * brightness))

    // Set RGB to grayscale value
    data[i] = adjustedGray // R
    data[i + 1] = adjustedGray // G
    data[i + 2] = adjustedGray // B
    // Alpha channel (data[i + 3]) remains unchanged
  }

  // Put the modified pixel data back
  ctx.putImageData(imageData, 0, 0)

  return canvas
}

/**
 * Apply grayscale effect using canvas composite operations (CORS-safe fallback)
 * This doesn't require reading pixels, so it works even with tainted canvases
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {HTMLImageElement} img - Source image
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} width - Width
 * @param {number} height - Height
 * @param {number} brightness - Brightness multiplier (simulated)
 */
const drawGrayscaleWithComposite = (
  ctx,
  img,
  x,
  y,
  width,
  height,
  brightness = 1.25
) => {
  // Save current composite state
  const prevComposite = ctx.globalCompositeOperation

  // Draw the original image
  ctx.drawImage(img, x, y, width, height)

  // Apply desaturation using 'saturation' composite mode with gray
  // This effectively removes color while preserving luminance
  ctx.globalCompositeOperation = 'saturation'
  ctx.fillStyle = 'hsl(0, 0%, 50%)'
  ctx.fillRect(x, y, width, height)

  // Apply slight brightness boost using 'lighter' or 'screen' mode
  if (brightness > 1) {
    ctx.globalCompositeOperation = 'screen'
    const boostAmount = Math.min(0.2, (brightness - 1) * 0.6)
    ctx.fillStyle = `rgba(255, 255, 255, ${boostAmount})`
    ctx.fillRect(x, y, width, height)
  }

  // Restore composite mode
  ctx.globalCompositeOperation = prevComposite
}

/**
 * Draw image with grayscale and brightness filter, using pixel manipulation on iOS
 * Falls back gracefully when CORS prevents pixel access
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {HTMLImageElement} img - Source image
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} width - Width
 * @param {number} height - Height
 * @param {boolean} applyGrayscale - Whether to apply grayscale
 */
const drawImageWithGrayscale = (
  ctx,
  img,
  x,
  y,
  width,
  height,
  applyGrayscale
) => {
  if (applyGrayscale && (isIOSSafari() || !supportsCanvasFilter())) {
    // Use pixel manipulation for iOS or when filter isn't supported
    try {
      // Use cached grayscale canvas if available and same image
      let grayscaleCanvas
      if (
        cachedGrayscaleCanvas.value &&
        cachedGrayscaleImageSrc.value === img.src
      ) {
        grayscaleCanvas = cachedGrayscaleCanvas.value
      } else {
        grayscaleCanvas = convertToGrayscalePixels(img, 1.25)
        cachedGrayscaleCanvas.value = grayscaleCanvas
        cachedGrayscaleImageSrc.value = img.src
      }
      ctx.drawImage(grayscaleCanvas, x, y, width, height)
    } catch (error) {
      // Fallback to canvas filter if pixel manipulation fails
      console.warn('Pixel manipulation failed, trying canvas filter:', error)
      try {
        ctx.filter = 'grayscale(1) brightness(1.25)'
        ctx.drawImage(img, x, y, width, height)
        ctx.filter = 'none'
      } catch (filterError) {
        // Try composite-based grayscale (works with tainted canvases)
        console.warn(
          'Canvas filter also failed, trying composite fallback:',
          filterError
        )
        try {
          drawGrayscaleWithComposite(ctx, img, x, y, width, height, 1.25)
        } catch (compositeError) {
          // Final fallback: just draw the image without grayscale
          console.warn(
            'All grayscale methods failed, drawing original:',
            compositeError
          )
          ctx.drawImage(img, x, y, width, height)
        }
      }
    }
  } else if (applyGrayscale) {
    // Use canvas filter for other browsers
    try {
      ctx.filter = 'grayscale(1) brightness(1.25)'
      ctx.drawImage(img, x, y, width, height)
      ctx.filter = 'none'
    } catch (error) {
      // Filter failed (possibly CORS), try composite fallback
      console.warn('Canvas filter failed, trying composite fallback:', error)
      ctx.filter = 'none'
      try {
        drawGrayscaleWithComposite(ctx, img, x, y, width, height, 1.25)
      } catch (compositeError) {
        // Final fallback
        console.warn(
          'Composite fallback also failed, drawing original:',
          compositeError
        )
        ctx.drawImage(img, x, y, width, height)
      }
    }
  } else {
    // No grayscale needed
    ctx.drawImage(img, x, y, width, height)
  }
}

const renderCanvas = async () => {
  const canvas = canvasRef.value
  const container = containerRef.value
  const img = imageRef.value

  if (!canvas || !container || !img) return

  const rect = container.getBoundingClientRect()

  // Don't render if container has no dimensions (iOS race condition)
  if (rect.width <= 0 || rect.height <= 0) {
    // Schedule retry
    requestAnimationFrame(renderCanvas)
    return
  }

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  // Use reduced DPR for better performance during navigation
  // Full DPR only needed for final export quality
  const dpr = Math.min(window.devicePixelRatio || 1, 2)

  // Set canvas size to match container
  canvas.width = rect.width * dpr
  canvas.height = rect.height * dpr
  ctx.scale(dpr, dpr)

  // Clear canvas with base color (darkened score color or default)
  const baseColor = props.scoreColor
    ? getDarkenedColor(props.scoreColor, 0.3)
    : '#111827'
  ctx.fillStyle = baseColor
  ctx.fillRect(0, 0, rect.width, rect.height)

  // Save context state
  ctx.save()

  // Move to center of canvas
  ctx.translate(rect.width / 2, rect.height / 2)

  // Apply rotation
  ctx.rotate((rotation.value * Math.PI) / 180)

  // Apply zoom (scales everything drawn after this)
  ctx.scale(zoom.value, zoom.value)

  // Apply pan offset (scaled by zoom already due to context transform)
  ctx.translate(panX.value, panY.value)

  // Calculate image dimensions to cover the 4:3 area at base zoom (1x)
  const canvasAspect = rect.width / rect.height
  const imgAspect = img.width / img.height

  let drawWidth, drawHeight

  // Cover the canvas (image fills the space, may be cropped)
  // Calculate at zoom=1, the ctx.scale() above handles actual zooming
  if (imgAspect > canvasAspect) {
    drawHeight = rect.height
    drawWidth = drawHeight * imgAspect
  } else {
    drawWidth = rect.width
    drawHeight = drawWidth / imgAspect
  }

  // Draw image with grayscale filter (iOS-compatible)
  drawImageWithGrayscale(
    ctx,
    img,
    -drawWidth / 2,
    -drawHeight / 2,
    drawWidth,
    drawHeight,
    !!props.scoreColor
  )

  // Restore context
  ctx.restore()

  // Apply color multiply blend on top of the grayscale image
  if (props.scoreColor) {
    ctx.globalCompositeOperation = 'multiply'
    ctx.fillStyle = props.scoreColor
    ctx.fillRect(0, 0, rect.width, rect.height)
    ctx.globalCompositeOperation = 'source-over'
  }

  // Draw text overlay if enabled
  if (showTextOverlay.value) {
    await drawTextOverlay(ctx, rect.width, rect.height)
  }
}

/**
 * Draw the Spectra logotype on the canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} targetWidth - Target width for the logo
 * @param {string} fillColor - Fill color for the logo
 */
const drawSpectraLogo = (ctx, x, y, targetWidth, fillColor = 'white') => {
  // Original SVG viewBox: 0 0 1379 303.9
  const originalWidth = 1379
  const originalHeight = 303.9
  const scale = targetWidth / originalWidth

  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)

  const path = new Path2D(SPECTRA_LOGO_PATH)
  ctx.fillStyle = fillColor
  ctx.fill(path)

  ctx.restore()

  // Return the actual height for positioning
  return originalHeight * scale
}

/**
 * Draw the Spectra "S" logo on the canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} x - X position (center)
 * @param {number} y - Y position (center)
 * @param {number} size - Size of the logo
 * @param {string} fillColor - Fill color for the logo
 */
const drawSpectraSLogo = (ctx, x, y, size, fillColor = 'white') => {
  // Original S logo viewBox: 0 0 226 243
  const originalWidth = 226
  const originalHeight = 243
  const scale = size / Math.max(originalWidth, originalHeight)

  ctx.save()
  // Center the logo
  ctx.translate(
    x - (originalWidth * scale) / 2,
    y - (originalHeight * scale) / 2
  )
  ctx.scale(scale, scale)

  const path = new Path2D(SPECTRA_S_LOGO_PATH)
  ctx.fillStyle = fillColor
  ctx.fill(path)

  ctx.restore()
}

/**
 * Ensure LandformWeb font is loaded before using in canvas
 * @returns {Promise<boolean>} - True if font is loaded/available
 */
const ensureFontLoaded = async () => {
  try {
    // Check if the font is already loaded
    if (document.fonts.check('16px LandformWeb')) {
      return true
    }

    // Try to load the font
    await document.fonts.load('16px LandformWeb')
    return document.fonts.check('16px LandformWeb')
  } catch (error) {
    console.warn('Could not load LandformWeb font:', error)
    return false
  }
}

/**
 * Get font stack with fallback - ensures LandformWeb if loaded, otherwise uses fallbacks
 * @param {number} size - Font size
 * @param {string} weight - Font weight (e.g., 'bold', 'normal')
 * @param {boolean} fontLoaded - Whether LandformWeb is loaded
 * @returns {string} - Complete font declaration
 */
const getFontString = (size, weight = 'normal', fontLoaded = false) => {
  const baseStack = 'system-ui, -apple-system, sans-serif'
  const fontFamily = fontLoaded ? `LandformWeb, ${baseStack}` : baseStack
  return `${weight === 'bold' ? 'bold ' : ''}${size}px ${fontFamily}`
}

/**
 * Draw text overlay on the canvas
 * Text sizes are optimized for legibility at different output sizes
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {number} scale - Scale factor for export (default 1)
 */
const drawTextOverlay = async (ctx, width, height, scale = 1) => {
  // Ensure font is loaded before drawing
  const fontLoaded = await ensureFontLoaded()
  // Use a modified scale that ensures minimum readable sizes for small outputs
  // For small sizes, we use relatively larger text proportions
  const effectiveScale = Math.max(scale, 0.5) // Don't go below 50% scale for text
  const smallSizeBoost = scale < 0.5 ? 1.3 : scale < 0.75 ? 1.15 : 1 // Boost text for small sizes

  const padding = Math.max(16, 18 * scale)
  const scoreSize = Math.max(20, 28 * scale * smallSizeBoost)
  const titleSize = Math.max(14, 20 * scale * smallSizeBoost)
  const yearSize = Math.max(9, 12 * scale * smallSizeBoost)
  const urlSize = Math.max(10, 14 * scale * smallSizeBoost)
  const usernameSize = Math.max(8, 11 * scale * smallSizeBoost)

  // Semi-transparent gradient at bottom for text readability
  const gradient = ctx.createLinearGradient(0, height * 0.6, 0, height)
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)')
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0.7)')
  ctx.fillStyle = gradient
  ctx.fillRect(0, height * 0.6, width, height * 0.4)

  // Draw score (bottom right)
  if (props.score !== null && props.score !== undefined) {
    const scoreText = Number(props.score).toFixed(1)
    ctx.font = getFontString(scoreSize, 'bold', fontLoaded)
    ctx.fillStyle = 'white'
    ctx.textAlign = 'right'
    ctx.textBaseline = 'alphabetic'
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
    ctx.shadowBlur = Math.max(2, 4 * scale)
    ctx.shadowOffsetX = Math.max(1, 2 * scale)
    ctx.shadowOffsetY = Math.max(1, 2 * scale)
    ctx.fillText(scoreText, width - padding, height - padding)
    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0
  }

  // Draw username (above title)
  if (props.username) {
    ctx.font = getFontString(usernameSize, 'bold', fontLoaded)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'bottom'
    ctx.fillText(
      `@${props.username}`,
      padding,
      height - padding - titleSize - Math.max(4, 4 * scale)
    )
  }

  // Draw title with year (bottom left)
  if (props.titleText) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
    ctx.shadowBlur = Math.max(2, 4 * scale)
    ctx.shadowOffsetX = Math.max(1, 2 * scale)
    ctx.shadowOffsetY = Math.max(1, 2 * scale)

    // Truncate title if too long
    let title = props.titleText
    const maxTitleWidth = width - padding * 2 - scoreSize * 2.5
    ctx.font = getFontString(titleSize, 'bold', fontLoaded)
    while (ctx.measureText(title).width > maxTitleWidth && title.length > 10) {
      title = title.slice(0, -4) + '...'
    }

    // Draw title
    ctx.fillStyle = 'white'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
    const titleY = height - padding
    // Calculate score text width using score font
    const scoreText = Number(props.score || 0).toFixed(1)
    const originalFont = ctx.font
    ctx.font = getFontString(scoreSize, 'bold', fontLoaded)
    const scoreTextWidth = ctx.measureText(scoreText).width
    ctx.font = originalFont
    const maxTitleWidthWithScore =
      width - padding * 4 - scoreTextWidth - Math.max(8, 16 * scale)
    while (
      ctx.measureText(title).width > maxTitleWidthWithScore &&
      title.length > 10
    ) {
      title = title.slice(0, -4) + '...'
    }
    ctx.fillText(title, padding, titleY)

    // Draw year next to title (smaller, lower contrast)
    if (props.year) {
      const titleWidth = ctx.measureText(title).width
      ctx.font = getFontString(yearSize, 'bold', fontLoaded)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
      ctx.fillText(
        `${props.year}`,
        padding + titleWidth + Math.max(4, 8 * scale),
        titleY
      )
    }

    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0
  }

  // Draw Spectra logotype (top right corner)
  const logoWidth = Math.max(60, 80 * scale)
  drawSpectraLogo(
    ctx,
    width - padding - logoWidth,
    padding,
    logoWidth,
    'rgba(255, 255, 255, 0.7)'
  )
}

/**
 * Convert HSL color string to hex
 * @param {string} hslString - HSL color string like "hsl(120, 50%, 50%)"
 * @returns {string} - Hex color string like "#00ff00"
 */
const hslToHex = (hslString) => {
  const hsl = parseHslColor(hslString)
  if (!hsl) return '#000000'

  const { h, s, l } = hsl
  const sNorm = s / 100
  const lNorm = l / 100

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = lNorm - c / 2

  let r = 0,
    g = 0,
    b = 0
  if (h < 60) {
    r = c
    g = x
    b = 0
  } else if (h < 120) {
    r = x
    g = c
    b = 0
  } else if (h < 180) {
    r = 0
    g = c
    b = x
  } else if (h < 240) {
    r = 0
    g = x
    b = c
  } else if (h < 300) {
    r = x
    g = 0
    b = c
  } else {
    r = c
    g = 0
    b = x
  }

  const toHex = (n) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Generate a QR code with rounded modules as a canvas
 * @param {string} url - The URL to encode
 * @param {number} size - Size of the QR code
 * @param {string} darkColor - Color for the dark modules (default black)
 * @param {string} lightColor - Color for the light modules (default white)
 * @param {number} borderWidth - Border width around the QR code
 * @returns {Promise<HTMLCanvasElement>} - The QR code as a canvas
 */
const generateQRCode = async (
  url,
  size = 150,
  darkColor = '#000000',
  lightColor = '#ffffff',
  borderWidth = 8
) => {
  // Generate QR code data matrix
  const qrData = await QRCode.create(url, {
    errorCorrectionLevel: 'H'
  })

  const modules = qrData.modules
  const moduleCount = modules.size
  const moduleSize = (size - borderWidth * 2) / moduleCount
  const cornerRadius = moduleSize * 0.35 // Rounded corner radius

  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')

  // Fill background with dark color (this becomes the "border" and module color)
  ctx.fillStyle = darkColor
  ctx.beginPath()
  ctx.roundRect(0, 0, size, size, borderWidth)
  ctx.fill()

  // Draw light-colored rounded rectangle as inner background
  ctx.fillStyle = lightColor
  ctx.beginPath()
  ctx.roundRect(
    borderWidth,
    borderWidth,
    size - borderWidth * 2,
    size - borderWidth * 2,
    borderWidth / 2
  )
  ctx.fill()

  // Draw each dark module as a rounded square
  ctx.fillStyle = darkColor
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (modules.get(row, col)) {
        const x = borderWidth + col * moduleSize
        const y = borderWidth + row * moduleSize

        // Draw rounded rectangle for each module
        ctx.beginPath()
        ctx.roundRect(x, y, moduleSize, moduleSize, cornerRadius)
        ctx.fill()
      }
    }
  }

  return canvas
}

/**
 * Draw text overlay with QR code on the canvas
 * Text sizes are optimized for legibility at different output sizes
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {number} scale - Scale factor for export (default 1)
 * @param {HTMLCanvasElement} qrCodeCanvas - Pre-generated QR code canvas
 * @param {string} scoreColor - The review's score color (hex string for QR light modules)
 */
const drawTextOverlayWithQR = async (
  ctx,
  width,
  height,
  scale = 1,
  qrCodeCanvas = null,
  scoreColor = null
) => {
  // Ensure font is loaded before drawing
  const fontLoaded = await ensureFontLoaded()
  // Use a modified scale that ensures minimum readable sizes for small outputs
  // For small sizes, we use relatively larger text proportions
  const smallSizeBoost = scale < 0.5 ? 1.3 : scale < 0.75 ? 1.15 : 1

  const padding = Math.max(16, 24 * scale)
  const scoreSize = Math.max(24, 48 * scale * smallSizeBoost)
  const titleSize = Math.max(14, 20 * scale * smallSizeBoost)
  const yearSize = Math.max(9, 12 * scale * smallSizeBoost)
  const urlSize = Math.max(10, 14 * scale * smallSizeBoost)
  const usernameSize = Math.max(8, 11 * scale * smallSizeBoost)
  const qrSize = Math.max(60, 120 * scale) // QR code needs to be scannable

  // Semi-transparent gradient at bottom for text readability
  const gradient = ctx.createLinearGradient(0, height * 0.5, 0, height)
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)')
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)')
  ctx.fillStyle = gradient
  ctx.fillRect(0, height * 0.5, width, height * 0.5)

  // Draw QR code with logo overlay (bottom right)
  if (qrCodeCanvas) {
    const qrX = width - padding - qrSize
    const qrY = height - padding - qrSize

    // Draw the pre-rendered QR code canvas (already has rounded corners and border)
    ctx.drawImage(qrCodeCanvas, qrX, qrY, qrSize, qrSize)

    // Draw S logo in center of QR code
    const logoSize = qrSize * 0.26
    const centerX = qrX + qrSize / 2
    const centerY = qrY + qrSize / 2

    // Black background circle for logo
    ctx.fillStyle = '#000000'
    ctx.beginPath()
    ctx.arc(centerX, centerY, logoSize * 0.6, 0, Math.PI * 2)
    ctx.fill()

    // Draw the S logo in the score color (matching QR light modules)
    const sLogoColor = scoreColor || '#ffffff'
    drawSpectraSLogo(ctx, centerX, centerY, logoSize, sLogoColor)
  }

  // Draw score (above QR code, right aligned)
  if (props.score !== null && props.score !== undefined) {
    const scoreText = Number(props.score).toFixed(1)
    ctx.font = getFontString(scoreSize, 'bold', fontLoaded)
    ctx.fillStyle = 'white'
    ctx.textAlign = 'right'
    ctx.textBaseline = 'bottom'
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
    ctx.shadowBlur = Math.max(2, 4 * scale)
    ctx.shadowOffsetX = Math.max(1, 2 * scale)
    ctx.shadowOffsetY = Math.max(1, 2 * scale)
    ctx.fillText(
      scoreText,
      width - padding,
      height - padding - qrSize - Math.max(8, 16 * scale)
    )
    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0
  }

  // Draw title with year (bottom left)
  if (props.titleText) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
    ctx.shadowBlur = Math.max(2, 4 * scale)
    ctx.shadowOffsetX = Math.max(1, 2 * scale)
    ctx.shadowOffsetY = Math.max(1, 2 * scale)

    // Truncate title if too long (account for QR code space)
    let title = props.titleText
    const maxTitleWidth =
      width - padding * 2 - qrSize - Math.max(16, 32 * scale)
    ctx.font = getFontString(titleSize, 'bold', fontLoaded)
    while (ctx.measureText(title).width > maxTitleWidth && title.length > 10) {
      title = title.slice(0, -4) + '...'
    }

    // Draw title
    ctx.fillStyle = 'white'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'bottom'
    const titleY = height - padding - urlSize - Math.max(4, 8 * scale)
    ctx.fillText(title, padding, titleY)

    // Draw year next to title (smaller, lower contrast)
    if (props.year) {
      const titleWidth = ctx.measureText(title).width
      ctx.font = getFontString(yearSize, 'normal', fontLoaded)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
      ctx.fillText(
        `(${props.year})`,
        padding + titleWidth + Math.max(4, 8 * scale),
        titleY
      )
    }

    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0
  }

  // Draw URL with username (below title)
  if (props.username) {
    ctx.font = getFontString(usernameSize, 'bold', fontLoaded)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'bottom'
    ctx.fillText(
      `spectra.reviews/@${props.username}`,
      padding,
      height - padding
    )
  }

  // Draw Spectra logotype (top right corner)
  const logoWidth = Math.max(60, 100 * scale)
  drawSpectraLogo(
    ctx,
    width - padding - logoWidth,
    padding,
    logoWidth,
    'rgba(255, 255, 255, 0.7)'
  )
}

const resetTransform = () => {
  panX.value = 0
  panY.value = 0
  zoom.value = 1
  rotation.value = 0
  renderCanvas()
}

// Apply a transform object to the current state
const applyTransform = (transform) => {
  if (!transform) return
  panX.value = transform.panX ?? 0
  panY.value = transform.panY ?? 0
  zoom.value = transform.zoom ?? 1
  rotation.value = transform.rotation ?? 0
}

// Find the index of an image by its file path
const findImageIndex = (imagePath) => {
  if (!imagePath || !props.images || props.images.length === 0) return 0
  const index = props.images.findIndex((img) => img.file_path === imagePath)
  return index >= 0 ? index : 0
}

const prevImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

const nextImage = () => {
  if (currentIndex.value < totalImages.value - 1) {
    currentIndex.value++
  }
}

// Toggle edit mode and show hint briefly
const toggleEditMode = () => {
  const enteringEditMode = !isEditMode.value
  isEditMode.value = !isEditMode.value

  // When entering edit mode, initialize canvas if needed
  if (enteringEditMode) {
    initializeCanvasForEdit()
  }

  // Show hint for 2 seconds when mode changes
  showHint.value = true
  setTimeout(() => {
    showHint.value = false
  }, 2000)
}

// Initialize canvas when entering edit mode or before export
// Returns a promise that resolves when the image is loaded and ready
const initializeCanvasForEdit = () => {
  return new Promise((resolve, reject) => {
    if (!currentImageUrl.value) {
      reject(new Error('No image URL'))
      return
    }

    // If canvas is already ready for this image, just render
    if (canvasReady.value && imageRef.value) {
      renderCanvas()
      resolve()
      return
    }

    // Load the image for canvas (with CORS for export)
    isLoading.value = true

    // Check cache first
    const cachedImg = imageCache.get(currentImageUrl.value)
    if (cachedImg && cachedImg.complete) {
      imageRef.value = cachedImg
      imageLoadedWithCors.value = true
      isLoading.value = false
      canvasReady.value = true
      renderCanvas()
      resolve()
      return
    }

    // Load image as blob for CORS support
    loadImageAsBlob(currentImageUrl.value)
      .then((blobUrl) => {
        cleanupBlobUrl()
        blobUrlRef.value = blobUrl

        const img = new Image()
        img.onload = () => {
          imageCache.set(currentImageUrl.value, img)
          imageRef.value = img
          imageLoadedWithCors.value = true
          isLoading.value = false
          canvasReady.value = true
          renderCanvas()
          resolve()
        }
        img.onerror = () => {
          console.error('Failed to load image for canvas')
          isLoading.value = false
          reject(new Error('Failed to load image'))
        }
        img.src = blobUrl
      })
      .catch((error) => {
        console.error('Failed to load image as blob:', error)
        isLoading.value = false
        reject(error)
      })
  })
}

// Check if tap/click is in the edge zones (for prev/next navigation)
const isInEdgeZone = (clientX) => {
  if (!containerRef.value) return false
  const rect = containerRef.value.getBoundingClientRect()
  const edgeWidth = rect.width * 0.15 // 15% on each side
  const relativeX = clientX - rect.left
  return relativeX < edgeWidth || relativeX > rect.width - edgeWidth
}

// Handle double-click on canvas (mouse)
const onDoubleClick = (e) => {
  // Don't toggle if clicking on edge zones (prev/next areas)
  if (isInEdgeZone(e.clientX)) return

  // Don't toggle if a handle drag just ended
  if (rotateHandleActive.value || zoomHandleActive.value) return

  toggleEditMode()
}

// Check for double-tap (touch) - called from onTouchEnd
const checkDoubleTap = (e) => {
  if (e.touches.length !== 0) return false // Only on final touch end

  const touch = e.changedTouches[0]
  if (!touch) return false

  // Don't trigger in edge zones
  if (isInEdgeZone(touch.clientX)) return false

  const now = Date.now()
  const timeDelta = now - lastTapTime.value
  const distX = Math.abs(touch.clientX - lastTapX.value)
  const distY = Math.abs(touch.clientY - lastTapY.value)

  // Check if this is a double-tap
  if (
    timeDelta < DOUBLE_TAP_DELAY &&
    distX < DOUBLE_TAP_DISTANCE &&
    distY < DOUBLE_TAP_DISTANCE
  ) {
    // Reset to prevent triple-tap
    lastTapTime.value = 0
    return true
  }

  // Record this tap for potential double-tap detection
  lastTapTime.value = now
  lastTapX.value = touch.clientX
  lastTapY.value = touch.clientY
  return false
}

// Button controls
const zoomIn = () => {
  zoom.value = Math.min(3, zoom.value + 0.25)
}

const zoomOut = () => {
  zoom.value = Math.max(1.0, zoom.value - 0.25)
}

const rotateStep = 15
const rotateLeft = () => {
  rotation.value -= rotateStep
}

const rotateRight = () => {
  rotation.value += rotateStep
}

// Calculate maximum pan bounds to keep image visible
const getMaxPanBounds = () => {
  if (!containerRef.value || !imageRef.value) return { maxX: 0, maxY: 0 }

  const rect = containerRef.value.getBoundingClientRect()
  const img = imageRef.value

  // Calculate image dimensions at current zoom
  const canvasAspect = rect.width / rect.height
  const imgAspect = img.width / img.height

  let drawWidth, drawHeight
  if (imgAspect > canvasAspect) {
    drawHeight = rect.height
    drawWidth = drawHeight * imgAspect
  } else {
    drawWidth = rect.width
    drawHeight = drawWidth / imgAspect
  }

  // Scale by current zoom
  const scaledWidth = drawWidth * zoom.value
  const scaledHeight = drawHeight * zoom.value

  // Allow movement until only 20% of image remains visible
  const minVisiblePercent = 0.2
  const maxX = (scaledWidth * (1 - minVisiblePercent)) / 2
  const maxY = (scaledHeight * (1 - minVisiblePercent)) / 2

  return { maxX, maxY }
}

// Constrain pan values within bounds
const constrainPan = (newPanX, newPanY) => {
  const { maxX, maxY } = getMaxPanBounds()
  return {
    x: Math.max(-maxX, Math.min(maxX, newPanX)),
    y: Math.max(-maxY, Math.min(maxY, newPanY))
  }
}

// Directional pan controls for non-touch devices
const panStep = 20
const panUp = () => {
  const constrained = constrainPan(panX.value, panY.value + panStep)
  panX.value = constrained.x
  panY.value = constrained.y
  renderCanvas()
}
const panDown = () => {
  const constrained = constrainPan(panX.value, panY.value - panStep)
  panX.value = constrained.x
  panY.value = constrained.y
  renderCanvas()
}
const panLeft = () => {
  const constrained = constrainPan(panX.value + panStep, panY.value)
  panX.value = constrained.x
  panY.value = constrained.y
  renderCanvas()
}
const panRight = () => {
  const constrained = constrainPan(panX.value - panStep, panY.value)
  panX.value = constrained.x
  panY.value = constrained.y
  renderCanvas()
}

// Block click events during/after drag to prevent modal close
const blockClick = (e) => {
  e.preventDefault()
  e.stopPropagation()
  e.stopImmediatePropagation()
}

// Handle rotation via corner drag (desktop only)
const startRotateHandle = (e, corner) => {
  if (!containerRef.value) return

  // Prevent event from bubbling to modal backdrop
  e.preventDefault()
  e.stopPropagation()

  rotateHandleActive.value = true
  handleStartRotation.value = rotation.value

  // Calculate angle from center of container to mouse position
  const rect = containerRef.value.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2
  handleStartAngle.value = Math.atan2(e.clientY - centerY, e.clientX - centerX)

  // Add global listeners with capture to intercept before modal handlers
  document.addEventListener('mousemove', onRotateHandleMove, { capture: true })
  document.addEventListener('mouseup', onRotateHandleEnd, { capture: true })
  // Also block click events that might fire after mouseup
  document.addEventListener('click', blockClick, { capture: true })
}

const onRotateHandleMove = (e) => {
  if (!rotateHandleActive.value || !containerRef.value) return

  // Prevent any other handlers from firing during drag
  e.preventDefault()
  e.stopPropagation()
  e.stopImmediatePropagation()

  const rect = containerRef.value.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2

  // Calculate current angle
  const currentAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX)
  const angleDelta = (currentAngle - handleStartAngle.value) * (180 / Math.PI)

  // Apply rotation (with optional snapping to 15-degree increments when holding shift)
  let newRotation = handleStartRotation.value + angleDelta
  if (e.shiftKey) {
    newRotation = Math.round(newRotation / 15) * 15
  }

  // Limit rotation to +/- 45 degrees
  rotation.value = Math.max(-45, Math.min(45, newRotation))
  renderCanvas()
}

const onRotateHandleEnd = (e) => {
  if (!rotateHandleActive.value) return

  // Prevent mouseup from triggering modal close
  e.preventDefault()
  e.stopPropagation()
  e.stopImmediatePropagation()

  rotateHandleActive.value = false
  document.removeEventListener('mousemove', onRotateHandleMove, {
    capture: true
  })
  document.removeEventListener('mouseup', onRotateHandleEnd, { capture: true })

  // Remove click blocker after a short delay to catch the click event
  setTimeout(() => {
    document.removeEventListener('click', blockClick, { capture: true })
  }, 100)
}

// Handle zoom via edge drag (desktop only)
const startZoomHandle = (e, edge) => {
  // Prevent event from bubbling to modal backdrop
  e.preventDefault()
  e.stopPropagation()

  zoomHandleActive.value = true
  activeEdge.value = edge
  handleStartY.value = e.clientY
  handleStartZoom.value = zoom.value

  // For horizontal edges, also track X for more intuitive dragging
  if (edge === 'l' || edge === 'r') {
    handleStartY.value = e.clientX
  }

  // Add global listeners with capture to intercept before modal handlers
  document.addEventListener('mousemove', onZoomHandleMove, { capture: true })
  document.addEventListener('mouseup', onZoomHandleEnd, { capture: true })
  // Also block click events that might fire after mouseup
  document.addEventListener('click', blockClick, { capture: true })
}

const onZoomHandleMove = (e) => {
  if (!zoomHandleActive.value) return

  // Prevent any other handlers from firing during drag
  e.preventDefault()
  e.stopPropagation()
  e.stopImmediatePropagation()

  let delta
  const sensitivity = 0.005 // Adjust zoom per pixel of movement

  // Calculate delta based on edge direction
  if (activeEdge.value === 't') {
    // Drag up = zoom in
    delta = (handleStartY.value - e.clientY) * sensitivity
  } else if (activeEdge.value === 'b') {
    // Drag down = zoom in
    delta = (e.clientY - handleStartY.value) * sensitivity
  } else if (activeEdge.value === 'l') {
    // Drag left = zoom in
    delta = (handleStartY.value - e.clientX) * sensitivity
  } else if (activeEdge.value === 'r') {
    // Drag right = zoom in
    delta = (e.clientX - handleStartY.value) * sensitivity
  }

  const newZoom = Math.max(1.0, Math.min(3, handleStartZoom.value + delta))
  zoom.value = newZoom
  renderCanvas()
}

const onZoomHandleEnd = (e) => {
  if (!zoomHandleActive.value) return

  // Prevent mouseup from triggering modal close
  e.preventDefault()
  e.stopPropagation()
  e.stopImmediatePropagation()

  zoomHandleActive.value = false
  activeEdge.value = null
  document.removeEventListener('mousemove', onZoomHandleMove, { capture: true })
  document.removeEventListener('mouseup', onZoomHandleEnd, { capture: true })

  // Remove click blocker after a short delay to catch the click event
  setTimeout(() => {
    document.removeEventListener('click', blockClick, { capture: true })
  }, 100)
}

// Mouse pan handlers
const startPan = (e) => {
  if (e.touches) return // Let touch handlers deal with touches
  showHint.value = false

  if (isEditMode.value) {
    // Edit mode: pan the image
    isPanning.value = true
    panStart.value = { x: e.clientX, y: e.clientY }
    panStartOffset.value = { x: panX.value, y: panY.value }
  } else {
    // Move mode: swipe to change images
    isSwipingImages.value = true
    swipeStartX.value = e.clientX
    swipeOffset.value = 0
  }
}

const onPan = (e) => {
  if (e.touches) return

  if (isEditMode.value && isPanning.value) {
    // Edit mode: pan the image
    const dx = e.clientX - panStart.value.x
    const dy = e.clientY - panStart.value.y

    const newPanX = panStartOffset.value.x + dx / zoom.value
    const newPanY = panStartOffset.value.y + dy / zoom.value
    const constrained = constrainPan(newPanX, newPanY)

    panX.value = constrained.x
    panY.value = constrained.y
    renderCanvas()
  } else if (!isEditMode.value && isSwipingImages.value) {
    // Move mode: track swipe offset for visual feedback
    swipeOffset.value = e.clientX - swipeStartX.value
  }
}

const endPan = () => {
  if (isEditMode.value) {
    isPanning.value = false
  } else if (isSwipingImages.value) {
    // Animate the carousel transition
    const containerW = containerRef.value?.offsetWidth || 300

    // Check if swipe exceeded threshold
    if (
      swipeOffset.value < -SWIPE_THRESHOLD &&
      currentIndex.value < totalImages.value - 1
    ) {
      // Swiped left - animate to next image
      isCarouselAnimating.value = true
      swipeOffset.value = -containerW
      setTimeout(() => {
        isCarouselAnimating.value = false
        currentIndex.value++
        swipeOffset.value = 0
      }, 300)
    } else if (swipeOffset.value > SWIPE_THRESHOLD && currentIndex.value > 0) {
      // Swiped right - animate to previous image
      isCarouselAnimating.value = true
      swipeOffset.value = containerW
      setTimeout(() => {
        isCarouselAnimating.value = false
        currentIndex.value--
        swipeOffset.value = 0
      }, 300)
    } else {
      // Snap back to current image
      isCarouselAnimating.value = true
      swipeOffset.value = 0
      setTimeout(() => {
        isCarouselAnimating.value = false
      }, 300)
    }
    isSwipingImages.value = false
  }
}

// Touch handlers with pinch-to-zoom and rotation
const getTouchDistance = (touches) => {
  const dx = touches[0].clientX - touches[1].clientX
  const dy = touches[0].clientY - touches[1].clientY
  return Math.sqrt(dx * dx + dy * dy)
}

const getTouchAngle = (touches) => {
  const dx = touches[1].clientX - touches[0].clientX
  const dy = touches[1].clientY - touches[0].clientY
  return Math.atan2(dy, dx) * (180 / Math.PI)
}

const onTouchStart = (e) => {
  showHint.value = false

  if (e.touches.length === 1) {
    if (isEditMode.value) {
      // Edit mode: single touch pan
      isPanning.value = true
      panStart.value = { x: e.touches[0].clientX, y: e.touches[0].clientY }
      panStartOffset.value = { x: panX.value, y: panY.value }
    } else {
      // Move mode: swipe to change images
      isSwipingImages.value = true
      swipeStartX.value = e.touches[0].clientX
      swipeOffset.value = 0
    }
  } else if (e.touches.length === 2 && isEditMode.value) {
    // Two touches - pinch zoom and rotate (only in edit mode)
    isPanning.value = false
    isSwipingImages.value = false
    touchState.value.startDistance = getTouchDistance(e.touches)
    touchState.value.startRotation = getTouchAngle(e.touches)
    touchState.value.startZoom = zoom.value
    touchState.value.startRotationValue = rotation.value
  }
  touchState.value.lastTouchCount = e.touches.length
}

const onTouchMove = (e) => {
  e.preventDefault()

  if (e.touches.length === 1) {
    if (isEditMode.value && isPanning.value) {
      // Edit mode: single touch pan
      const dx = e.touches[0].clientX - panStart.value.x
      const dy = e.touches[0].clientY - panStart.value.y

      const newPanX = panStartOffset.value.x + dx / zoom.value
      const newPanY = panStartOffset.value.y + dy / zoom.value
      const constrained = constrainPan(newPanX, newPanY)

      panX.value = constrained.x
      panY.value = constrained.y
      renderCanvas()
    } else if (!isEditMode.value && isSwipingImages.value) {
      // Move mode: track swipe offset
      swipeOffset.value = e.touches[0].clientX - swipeStartX.value
    }
  } else if (e.touches.length === 2 && isEditMode.value) {
    // Pinch zoom (only in edit mode)
    const currentDistance = getTouchDistance(e.touches)
    const scale = currentDistance / touchState.value.startDistance
    zoom.value = Math.max(1.0, Math.min(3, touchState.value.startZoom * scale))

    // Two-finger rotation (limited to +/- 45 degrees)
    const currentAngle = getTouchAngle(e.touches)
    const angleDelta = currentAngle - touchState.value.startRotation
    const newRotation = touchState.value.startRotationValue + angleDelta
    rotation.value = Math.max(-45, Math.min(45, newRotation))

    renderCanvas()
  }
}

const onTouchEnd = (e) => {
  if (e.touches.length === 0) {
    // Check for double-tap to toggle mode (only if no significant movement)
    const wasSwipe = Math.abs(swipeOffset.value) > 10
    const wasPan =
      isPanning.value &&
      (Math.abs(panX.value - panStartOffset.value.x) > 10 ||
        Math.abs(panY.value - panStartOffset.value.y) > 10)

    if (isEditMode.value) {
      isPanning.value = false
    } else if (isSwipingImages.value) {
      // Animate the carousel transition
      const containerW = containerRef.value?.offsetWidth || 300

      // Check if swipe exceeded threshold
      if (
        swipeOffset.value < -SWIPE_THRESHOLD &&
        currentIndex.value < totalImages.value - 1
      ) {
        // Swiped left - animate to next image
        isCarouselAnimating.value = true
        swipeOffset.value = -containerW
        setTimeout(() => {
          isCarouselAnimating.value = false
          currentIndex.value++
          swipeOffset.value = 0
        }, 300)
      } else if (
        swipeOffset.value > SWIPE_THRESHOLD &&
        currentIndex.value > 0
      ) {
        // Swiped right - animate to previous image
        isCarouselAnimating.value = true
        swipeOffset.value = containerW
        setTimeout(() => {
          isCarouselAnimating.value = false
          currentIndex.value--
          swipeOffset.value = 0
        }, 300)
      } else {
        // Snap back to current image
        isCarouselAnimating.value = true
        swipeOffset.value = 0
        setTimeout(() => {
          isCarouselAnimating.value = false
        }, 300)
      }
      isSwipingImages.value = false
    }

    // Check for double-tap only if there was no significant gesture
    if (!wasSwipe && !wasPan && checkDoubleTap(e)) {
      toggleEditMode()
    }
  } else if (
    e.touches.length === 1 &&
    touchState.value.lastTouchCount === 2 &&
    isEditMode.value
  ) {
    // Went from 2 touches to 1, restart pan (only in edit mode)
    isPanning.value = true
    panStart.value = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    panStartOffset.value = { x: panX.value, y: panY.value }
  }
  touchState.value.lastTouchCount = e.touches.length
}

// Mouse wheel zoom (only in edit mode)
const onWheel = (e) => {
  if (!isEditMode.value) return
  e.preventDefault()
  const delta = e.deltaY > 0 ? -0.1 : 0.1
  zoom.value = Math.max(1.0, Math.min(3, zoom.value + delta))
}

/**
 * Export the canvas as a JPEG blob
 * @param {number} width - Export width (default 1200)
 * @param {number} height - Export height (default 900)
 * @param {string} variant - Export variant:
 *   - 'clean': Base image only (no text overlay)
 *   - 'plain': Base image (no text overlay)
 *   - 'with_text' | 'full': Base image + text overlay
 * @returns {Promise<Blob>} JPEG blob
 */
const exportImage = async (width = 1200, height = 900, variant = 'plain') => {
  // Use stored dimensions — containerRef may be null if component unmounted
  const containerW = containerWidth.value || 400
  const containerH = containerHeight.value || 300

  // Ensure canvas is initialized before export (may be in browse mode)
  if (!canvasReady.value || !imageRef.value) {
    await initializeCanvasForEdit()
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const img = imageRef.value

  if (!img) {
    throw new Error('No image loaded')
  }

  canvas.width = width
  canvas.height = height

  // Clear canvas with base color (darkened score color or default)
  const baseColor = props.scoreColor
    ? getDarkenedColor(props.scoreColor, 0.3)
    : '#111827'
  ctx.fillStyle = baseColor
  ctx.fillRect(0, 0, width, height)

  // Apply transforms
  ctx.save()
  ctx.translate(width / 2, height / 2)
  ctx.rotate((rotation.value * Math.PI) / 180)
  ctx.scale(zoom.value, zoom.value)
  ctx.translate(
    panX.value * (width / containerW),
    panY.value * (height / containerH)
  )

  // Calculate image dimensions at zoom=1, the ctx.scale() above handles actual zooming
  const canvasAspect = width / height
  const imgAspect = img.width / img.height

  let drawWidth, drawHeight

  if (imgAspect > canvasAspect) {
    drawHeight = height
    drawWidth = drawHeight * imgAspect
  } else {
    drawWidth = width
    drawHeight = drawWidth / imgAspect
  }

  // Draw image with grayscale filter (iOS-compatible)
  // This MUST be applied before drawing the image to ensure it's grayscale
  drawImageWithGrayscale(
    ctx,
    img,
    -drawWidth / 2,
    -drawHeight / 2,
    drawWidth,
    drawHeight,
    !!props.scoreColor
  )

  // Restore context (this restores transforms)
  ctx.restore()

  // Apply color multiply blend on top of the grayscale image
  // This must happen AFTER the image is drawn with grayscale filter
  if (props.scoreColor) {
    ctx.globalCompositeOperation = 'multiply'
    ctx.fillStyle = props.scoreColor
    ctx.fillRect(0, 0, width, height)
    ctx.globalCompositeOperation = 'source-over'
  }

  // Calculate scale factor (based on export size vs display size)
  const scale = width / containerW

  // Draw text overlay based on variant (skip for 'clean' and 'plain')
  if (variant === 'with_text' || variant === 'full') {
    await drawTextOverlay(ctx, width, height, scale)
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to export image'))
        }
      },
      'image/jpeg',
      0.9
    )
  })
}

/**
 * Export all variants for sharing/saving
 * Returns clean and full versions
 * @param {Object} options
 * @param {number} options.width - Export width (default 1200)
 * @param {number} options.height - Export height (default 900)
 * @param {Function} options.onProgress - Progress callback
 * @returns {Promise<Object>} { clean: Blob, full: Blob }
 */
const exportAllVariants = async (options = {}) => {
  const { width = 1200, height = 900, onProgress } = options

  const results = {}
  const totalSteps = 2
  let currentStep = 0

  // Export clean version
  if (onProgress)
    onProgress(currentStep / totalSteps, 'Exporting clean version...')
  results.clean = await exportImage(width, height, 'clean')
  currentStep++

  // Export full version with text
  if (onProgress)
    onProgress(currentStep / totalSteps, 'Exporting full version...')
  results.full = await exportImage(width, height, 'full')
  currentStep++

  if (onProgress) onProgress(1, 'Complete')

  return results
}

// Size presets for different sharing targets
// All maintain 4:3 aspect ratio
const SIZE_PRESETS = {
  // Large - Full resolution for high-quality shares, website embeds
  large: { width: 1200, height: 900, label: 'Large' },
  // Medium - Good for most social media, messaging apps
  medium: { width: 800, height: 600, label: 'Medium' },
  // Small - Thumbnails, previews, low-bandwidth scenarios
  small: { width: 400, height: 300, label: 'Small' },
  // XSmall - Tiny thumbnails, list views, minimal bandwidth
  xsmall: { width: 200, height: 150, label: 'Extra Small' }
}

/**
 * Export all size variants for a given image variant type
 * @param {string} variant - 'plain' | 'with_text' | 'social_share'
 * @returns {Promise<Object>} - Object with size keys and blob values
 */
const exportAllSizes = async (variant = 'plain') => {
  const results = {}

  for (const [sizeName, { width, height }] of Object.entries(SIZE_PRESETS)) {
    try {
      const blob = await exportImage(width, height, variant)
      results[sizeName] = blob
    } catch (error) {
      console.error(`Failed to export ${variant} at ${sizeName}:`, error)
      results[sizeName] = null
    }
  }

  return results
}

// Computed to check if any drag operation is active
const isDragging = computed(
  () =>
    rotateHandleActive.value ||
    zoomHandleActive.value ||
    isPanning.value ||
    isSwipingImages.value
)

// Expose export methods to parent
defineExpose({
  exportImage,
  exportAllVariants,
  exportAllSizes,
  SIZE_PRESETS,
  isDragging,
  getCurrentTransform: () => ({
    panX: panX.value,
    panY: panY.value,
    zoom: zoom.value,
    rotation: rotation.value,
    imagePath: currentImagePath.value
  })
})

// Watchers
watch(
  [panX, panY, zoom, rotation, showTextOverlay, () => props.scoreColor],
  () => {
    // Only render canvas when in edit mode and canvas is ready
    // In browse mode, the preview img handles transforms via CSS
    if (isEditMode.value && canvasReady.value) {
      renderCanvas()
    }
  }
)

// Re-constrain pan when zoom changes
watch(zoom, () => {
  const constrained = constrainPan(panX.value, panY.value)
  panX.value = constrained.x
  panY.value = constrained.y
})

watch(currentIndex, () => {
  // Reset transform when changing images
  resetTransform()
  // Mark canvas as needing re-initialization for new image
  canvasReady.value = false

  // Only load for canvas if in edit mode, otherwise preview img handles it
  if (isEditMode.value) {
    loadImageOptimized()
  }
})

watch(
  () => props.images,
  () => {
    if (props.images?.length > 0) {
      // If we have initial state to apply and haven't applied it yet
      if (
        !hasAppliedInitialState.value &&
        (props.initialImagePath || props.initialTransform)
      ) {
        hasAppliedInitialState.value = true
        skipNextReset.value = true // Tell loadImage to apply transform instead of reset

        // If we have an initial image path, find and select it
        if (props.initialImagePath) {
          const initialIndex = findImageIndex(props.initialImagePath)
          if (initialIndex !== currentIndex.value) {
            currentIndex.value = initialIndex
            return // The currentIndex watcher will trigger loadImage
          }
        }
      }
      // Only load for canvas if in edit mode or if we have initial transform
      // Otherwise the preview img element handles display
      if (isEditMode.value || props.initialTransform) {
        loadImageOptimized()
      }
    }
  },
  { immediate: true }
)

// Watch for initialTransform changes (in case it's loaded asynchronously)
watch(
  () => props.initialTransform,
  (newTransform) => {
    // If we receive a transform after mount but before applying initial state
    if (newTransform && !hasAppliedInitialState.value) {
      hasAppliedInitialState.value = true
      applyTransform(newTransform)
      // Only render canvas if it's ready (edit mode will handle it otherwise)
      if (canvasReady.value && imageRef.value) {
        renderCanvas()
      }
    }
  }
)

// Lifecycle
onMounted(() => {
  // Detect touch device
  isTouchDevice.value = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  // Initial dimension update - use double RAF for iOS timing
  // First RAF waits for render, second ensures layout is complete
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      updateContainerDimensions()
    })
  })

  // Handle resize
  const resizeObserver = new ResizeObserver((entries) => {
    // Check if entry has valid dimensions before updating
    for (const entry of entries) {
      if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
        containerWidth.value = entry.contentRect.width
        containerHeight.value = entry.contentRect.height
        // Only render canvas when in edit mode
        if (isEditMode.value && canvasReady.value) {
          renderCanvas()
        }
      }
    }
  })

  if (containerRef.value) {
    resizeObserver.observe(containerRef.value)
  }

  // Hide hint after 3 seconds
  setTimeout(() => {
    showHint.value = false
  }, 3000)

  onUnmounted(() => {
    resizeObserver.disconnect()
    cleanupBlobUrl() // Clean up any blob URLs

    // Clear image cache to prevent memory leaks
    imageCache.clear()
    // Clean up any active handle event listeners (must match capture option)
    document.removeEventListener('mousemove', onRotateHandleMove, {
      capture: true
    })
    document.removeEventListener('mouseup', onRotateHandleEnd, {
      capture: true
    })
    document.removeEventListener('mousemove', onZoomHandleMove, {
      capture: true
    })
    document.removeEventListener('mouseup', onZoomHandleEnd, { capture: true })
    document.removeEventListener('click', blockClick, { capture: true })
  })
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
